import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import api from "../../../chat/api";
import toast from "react-hot-toast";
import {
  AIAvatarIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  CopyIcon,
  CheckIcon,
  SendIcon,
} from "./icons";
import "./ChatWithProposal.css";
import MemoizedMarkdown from "../MemoizedMarkdown";
import { emitPlanLimit } from "../../../../events/planLimitEvents";

const WS_BASE_URL = process.env.REACT_APP_WS_URL || "wss://stagingbackend.docusensa.com";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;

const ChatWithProposal = ({ 
  proposalData, 
  projectName, 
  messages, 
  setMessages,
  onRefreshProposal,
  isActive = true
}) => {
  const [displayName, setDisplayName] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isChatInitializing, setIsChatInitializing] = useState(false);
  const [token, setToken] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const messageQueueRef = useRef([]);
  const streamingMessageRef = useRef("");
  const [streamingMessage, setStreamingMessage] = useState("");
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const hasInitializedChatRef = useRef(false);
  const shouldRefreshRef = useRef(false); // Track if we need to refresh after streaming
  
  const location = useLocation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, pendingApproval]);

  // Extract folderId from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const projectIndex = pathParts.indexOf('project');
    
    if (projectIndex !== -1 && pathParts[projectIndex + 1]) {
      const extractedFolderId = pathParts[projectIndex + 1];
      setFolderId(extractedFolderId);
    }
  }, [location.pathname]);

  // Initialize component and get token
  useEffect(() => {
    isMountedRef.current = true;

    const full_name = localStorage.getItem("full_name") || localStorage.getItem("fullName") || "";
    const username = localStorage.getItem("username") || "";
    setDisplayName(full_name || username);

    const cookieToken = Cookies.get("access_token");
    if (cookieToken) {
      setToken(cookieToken);
    } else {
      toast.error("Authentication token not found");
    }

    return () => {
      isMountedRef.current = false;
      
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (wsRef.current) {
        console.log("Cleaning up WebSocket on unmount");
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
    };
  }, []);

  // Disconnect WebSocket when tab becomes inactive
  useEffect(() => {
    if (!isActive && wsRef.current) {
      console.log("Tab inactive, closing WebSocket connection");
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      wsRef.current.close(1000, "Tab switched");
      wsRef.current = null;
      setIsConnected(false);
      isConnectingRef.current = false;
    }
  }, [isActive]);

  // Initialize chat session
  useEffect(() => {
    if (!token || !isActive || hasInitializedChatRef.current || chatId) {
      return;
    }

    const initializeChat = async () => {
      setIsChatInitializing(true);
      hasInitializedChatRef.current = true;
      
      try {
        console.log("Initializing chat session...");
        const response = await api.post(
          "/api/v1/copilot/new-chat/",
          {
            chat_module: "rfp_kb_copilot"
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.chat_id) {
          console.log("Chat session initialized:", response.data.chat_id);
          setChatId(response.data.chat_id);
        } else {
          toast.error("Failed to get chat session ID");
          hasInitializedChatRef.current = false;
        }
      } catch (error) {
        console.error("Chat initialization error:", error);
        hasInitializedChatRef.current = false;
        toast.error(
          error.response?.data?.message || 
          "Failed to initialize chat. Please try again."
        );
      } finally {
        setIsChatInitializing(false);
      }
    };

    initializeChat();
  }, [token, isActive, chatId]);

  const handleWebSocketMessage = useCallback((data) => {
    if (!isMountedRef.current) return;

    // Handle approval required
    if (data.type === "approval_required") {
      // FIX 1: Check if there was any streaming text before approval was requested
      const currentText = streamingMessageRef.current;
      if (currentText && currentText.trim()) {
        const preApprovalMessage = {
          type: "assistant",
          content: currentText,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, preApprovalMessage]);
      }
      
      // Clear streaming buffers
      streamingMessageRef.current = "";
      setStreamingMessage("");

      setApprovalMessage(data.message || "This action requires your approval to proceed.");
      setPendingApproval(true);
      setIsStreaming(false);
      setIsLoading(false);
      return;
    }

    // A tool hit a plan limit; the reply explains it, this opens the upgrade popup.
    if (data.type === "plan_limit") {
      emitPlanLimit(data.message);
      return;
    }

    // Handle info message - prepares for streaming and marks refresh needed
    if (data.type === "info") {
      // FIX 2: Delay the refresh. Mark it as needed, but don't call it yet.
      shouldRefreshRef.current = true;
      
      setIsStreaming(true);
      streamingMessageRef.current = "";
      setStreamingMessage("");
      return;
    }

    if (data.type === "progress") {
      streamingMessageRef.current += data.message || data.chunk || "";
      setStreamingMessage(streamingMessageRef.current);
      return;
    }

    if (data.type === "final") {
      return;
    }

    if (data.type === "completed" || data.status === "completed") {
      const finalText = streamingMessageRef.current;

      if (finalText.trim()) {
        const botMessage = {
          type: "assistant",
          content: finalText,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);
      }

      // FIX 2: Execute the delayed refresh here, after streaming is done
      // CHANGE: Refresh only when tool_call === true
      if (shouldRefreshRef.current && data.tool_call === true) {
        if (onRefreshProposal) {
          onRefreshProposal();
        }
      }
      shouldRefreshRef.current = false;

      streamingMessageRef.current = "";
      setStreamingMessage("");
      setIsStreaming(false);
      setIsLoading(false);
      return;
    }

    if (data.type === "error" || data.error) {
      if (data.code === "plan_limit") emitPlanLimit(data.message || data.error);
      const errorMessage = {
        type: "assistant",
        content: data.error || data.message || "An error occurred. Please try again.",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);

      streamingMessageRef.current = "";
      setStreamingMessage("");
      setIsStreaming(false);
      setIsLoading(false);
      shouldRefreshRef.current = false; // Reset refresh flag on error
      toast.error(data.error || "An error occurred");
      return;
    }
  }, [onRefreshProposal, setMessages]);

  const connectWebSocket = useCallback(() => {
    if (!isActive) {
      console.log("Tab not active, skipping WebSocket connection");
      return;
    }

    if (isConnectingRef.current) {
      console.log("Connection already in progress, skipping...");
      return;
    }

    if (!folderId || !chatId || !token || !isMountedRef.current) {
      console.log("Missing required data for WebSocket connection");
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    if (wsRef.current) {
      console.log("Closing existing WebSocket connection");
      wsRef.current.close(1000, "Reconnecting");
      wsRef.current = null;
    }

    isConnectingRef.current = true;

    try {
      const wsUrl = `${WS_BASE_URL}/ws/rfp-proposal-copilot/${folderId}/${chatId}/?token=${token}`;
      console.log("Connecting to WebSocket:", wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected successfully");
        setIsConnected(true);
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;

        if (messageQueueRef.current.length > 0) {
          console.log(`Sending ${messageQueueRef.current.length} queued messages`);
          while (messageQueueRef.current.length > 0) {
            const queuedMessage = messageQueueRef.current.shift();
            ws.send(JSON.stringify(queuedMessage));
          }
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
          toast.error("Error processing message");
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        isConnectingRef.current = false;
        wsRef.current = null;

        if (event.code !== 1000 && isMountedRef.current && isActive) {
          reconnectAttemptsRef.current += 1;
          
          if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
            console.log(`Reconnection attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current && isActive) {
                connectWebSocket();
              }
            }, RECONNECT_DELAY);
          } else {
            console.error("Max reconnection attempts reached");
            toast.error("Connection lost. Please refresh the page.");
          }
        }
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      isConnectingRef.current = false;
      toast.error("Failed to establish connection");
    }
  }, [folderId, chatId, token, isActive, handleWebSocketMessage]);

  useEffect(() => {
    if (chatId && token && folderId && !isChatInitializing && isActive) {
      console.log("Initiating WebSocket connection (tab is active)");
      connectWebSocket();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [chatId, token, folderId, isChatInitializing, isActive, connectWebSocket]);

  const sendWebSocketMessage = useCallback((messageText) => {
    if (!folderId || !chatId) {
      toast.error("Chat session not ready. Please wait.");
      return;
    }

    const payload = {
      client_message: messageText
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("Sending message via WebSocket");
      wsRef.current.send(JSON.stringify(payload));
    } else {
      console.log("WebSocket not ready, queueing message");
      messageQueueRef.current.push(payload);
      
      if (!isConnectingRef.current && isActive) {
        connectWebSocket();
      }
    }
  }, [folderId, chatId, isActive, connectWebSocket]);

  const handleApprovalAction = useCallback((action) => {
    const actionPayload = {
      action: action
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(actionPayload));
    } else {
      toast.error("WebSocket not connected. Please try again.");
      return;
    }

    // Add user's action as a message
    const userActionMessage = {
      type: "user",
      content: action === "approve" ? "Implement" : "Deny Action",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userActionMessage]);

    // Reset approval state and set loading
    setPendingApproval(false);
    setApprovalMessage("");
    setIsLoading(true);
  }, [setMessages]);

  const handleTextareaResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    handleTextareaResize();
  };

  const handleCopyMessage = async (content, index) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      toast.success("Copied to clipboard!");
      setTimeout(() => {
        setCopiedIndex(null);
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy message");
    }
  };

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isChatInitializing || pendingApproval) {
      return;
    }

    const userMessage = inputValue.trim();
    setInputValue("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    setMessages((prev) => [
      ...prev,
      {
        type: "user",
        content: userMessage,
        timestamp: new Date(),
      },
    ]);

    setIsLoading(true);
    streamingMessageRef.current = "";
    setStreamingMessage("");

    try {
      sendWebSocketMessage(userMessage);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");

      const errorMessage = {
        type: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
    }
  }, [inputValue, isLoading, isChatInitializing, pendingApproval, sendWebSocketMessage, setMessages]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-4 pr-2 space-y-4">
        {isChatInitializing ? (
          <div className="flex items-start gap-[4px] pr-2">
            <div className="w-8 h-8 rounded-full border border-[#E6E6E6] flex items-center justify-center flex-shrink-0 animate-pulse-scale">
              <AIAvatarIcon />
            </div>
            <div className="flex-1">
              <p className="text-[#18101C] font-outfit text-[15px] pt-1 font-medium">
                Starting new chat...
              </p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-start gap-[4px] pr-2">
            <div className="w-8 h-8 rounded-full border border-[#E6E6E6] flex items-center justify-center flex-shrink-0">
              <AIAvatarIcon />
            </div>
            <div className="flex-1">
              <p className="text-[#18101C] font-outfit text-[15px] pt-1 font-medium">
                Hi {displayName ? displayName : "User"}! How can I help you today?
              </p>
            </div>
          </div>
        ) : null}

        {messages.map((message, index) => (
          <div key={index} className="pr-2">
            {message.type === "user" ? (
              <div className="flex justify-end">
                <div className="bg-[#ECE4FA] text-[#3F3F46] px-[10px] py-[7px] rounded-2xl rounded-tr-sm max-w-[100%]">
                  <p className="text-[14px] font-outfit font-normal leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-[4px]">
                <div className="w-8 h-8 rounded-full border border-[#E6E6E6] flex items-center justify-center flex-shrink-0">
                  <AIAvatarIcon />
                </div>
                <div className="flex-1">
                  <div className="bg-[#F7F7F9] px-[10px] py-[7px] rounded-2xl rounded-tl-sm max-w-[100%]">
                    <div className="text-[#18101C] text-[14px] font-outfit font-normal leading-relaxed">
                      <MemoizedMarkdown content={message.content} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2 ml-1">
                    <button
                      className="transition-colors"
                      aria-label="Like response"
                    >
                      <ThumbsUpIcon />
                    </button>

                    <button
                      className="transition-colors"
                      aria-label="Dislike response"
                    >
                      <ThumbsDownIcon />
                    </button>

                    <button
                      onClick={() => handleCopyMessage(message.content, index)}
                      className="transition-colors relative"
                      aria-label="Copy response"
                    >
                      {copiedIndex === index ? <CheckIcon /> : <CopyIcon />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {(isLoading || isStreaming) && (
          <div className="pr-2">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full border border-[#E6E6E6] flex items-center justify-center flex-shrink-0 animate-pulse-scale">
                <AIAvatarIcon />
              </div>
              <div className="flex-1">
                {streamingMessage ? (
                  <div className="bg-[#F7F7F9] px-[10px] py-[7px] rounded-2xl rounded-tl-sm max-w-[100%]">
                    <div className="text-[#18101C] text-[14px] font-outfit font-normal leading-relaxed">
                      <MemoizedMarkdown content={streamingMessage} />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 py-3">
                    <div className="w-3 h-3 bg-[#C95EBE] rounded-full animate-dot-pulse"></div>
                    <div className="w-3 h-3 bg-[#C95EBE] rounded-full animate-dot-pulse animation-delay-200"></div>
                    <div className="w-3 h-3 bg-[#C95EBE] rounded-full animate-dot-pulse animation-delay-400"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {pendingApproval && (
          <div className="pr-2">
            <div className="flex items-start gap-[4px]">
              <div className="flex-1">
                <div className="max-w-[100%]">
                  <div className="bg-[#F7F7F7] border border-[#00000026] rounded-xl p-3 mt-2">
                    <p className="text-[#3F3F46] font-outfit text-[14px] font-medium mb-3">
                      Proposed Action (Requires Approval)
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprovalAction("deny")}
                        className="flex-1 bg-[#BD3A3A] text-white font-outfit font-medium text-[14px] py-2 w-[40%] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6 6L14 14M6 14L14 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                        Deny Action
                      </button>
                      <button
                        onClick={() => handleApprovalAction("approve")}
                        className="flex-1 bg-[#279643] text-white font-outfit font-medium text-[14px] py-2 w-[40%] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16 6L8 14L4 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Implement
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="pt-4 pb-2">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder={
              pendingApproval
                ? "Approval Pending..."
                : isChatInitializing
                ? "Starting new chat..."
                : isLoading
                ? "Copilot is responding..."
                : "Ask me anything..."
            }
            rows={1}
            className="w-full px-5 py-3.5 pr-14 bg-[#FFFFFF] border border-[#E6E6E6] rounded-[14px] font-normal font-outfit text-[14px] text-[#18101C] placeholder:font-normal placeholder:text-[#52525B] focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent transition-all resize-none overflow-y-auto box-border"
            style={{
              minHeight: "52px",
              maxHeight: "150px",
            }}
            disabled={isLoading || isChatInitializing || pendingApproval}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading || isChatInitializing || pendingApproval}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              inputValue.trim() && !isLoading && !isChatInitializing && !pendingApproval
                ? "bg-[#C95EBE] hover:bg-[#BB42AE] cursor-pointer"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWithProposal;
